/**
 * Demo page showcasing selections viewer for IBS 2023
 * @module
 *
 */
import { createFileRoute } from "@tanstack/react-router";
import { useSearch } from "@tanstack/react-router";
import type { OptionPropsWithImage } from "lib/components/demo/Ibs2023LandingPage";
import Ibs2023LandingPage from "lib/components/demo/Ibs2023LandingPage";
import SelectionsViewer from "lib/components/selections/SelectionsViewer";
import useSelectionsViewer from "lib/components/selections/useSelectionsViewer";
import type { SelectionsViewerContextProps } from "lib/state/SelectionsViewerContext";
import SelectionsViewerContext from "lib/state/SelectionsViewerContext";
import { getSceneVersionManifestJson } from "lib/storage/selectionsStorage";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { parseCompositorManifest } from "selections-compositor/state/selections";
import Loading from "ui/components/Loading";
import useAppContext from "ui/hooks/useAppContext";
import { useAppNavigate } from "ui/hooks/useAppNavigate";

const SCENE_OPTIONS: OptionPropsWithImage[] = [
  {
    label: "Owner's Bath",
    value: "20230119-owners_bathroom",
    imageUrl: "https://photoreal-test.s3.amazonaws.com/demos/ibs2023-selections-orion-bathroom.png",
  },
  {
    label: "Chef's Kitchen",
    value: "20230119-kitchen",
    imageUrl: "https://photoreal-test.s3.amazonaws.com/demos/ibs2023-selections-orion-kitchen.png",
  },
];

function PhotorealSelections() {
  const query = useSearch({ strict: false }) as Record<string, string>;
  const navigate = useAppNavigate();
  const { setUserNotification } = useAppContext();

  const sceneVersionId = query?.scene as string;

  const {
    manifest,
    setManifest,
    activeSelections,
    scene,
    setScene,
    menuItems,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
  } = useSelectionsViewer({
    onActiveSelections: async () => {
      setTilesRefreshLastTimestamp(Date.now());
    },
  });

  const [tilesRefreshLastTimestamp, setTilesRefreshLastTimestamp] = useState<number>();

  useEffect(() => {
    async function fetchAndSetScene() {
      try {
        const manifestJson = await getSceneVersionManifestJson(sceneVersionId);
        if (manifestJson) {
          const newManifest = parseCompositorManifest(manifestJson);
          setManifest(newManifest);
        }
      } catch {
        setUserNotification({
          message: "Cannot load scene",
          messageType: "error",
        });
      }
    }

    if (sceneVersionId) {
      void fetchAndSetScene();
    }
  }, [sceneVersionId]);

  const selectionsViewerContext: SelectionsViewerContextProps = {
    manifest,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
    menuItems,
    activeSelections,
  };

  if (!sceneVersionId) {
    return (
      <>
        <Head>
          <title>{getPageTitle("Higharc Photoreal - Selections")}</title>
        </Head>

        <div style={{ display: sceneVersionId ? "none" : "inherit" }}>
          <Ibs2023LandingPage
            headline="Enter the Design Center"
            secondaryMessage="Tap a room below to explore the first, data-driven selection experience. Empower your buyers in real time."
            options={SCENE_OPTIONS}
            onSelect={(value) => navigate({ search: { scene: value } as never })}
          />
        </div>
      </>
    );
  }
  if (!manifest) {
    return <Loading />;
  }
  return (
    <SelectionsViewerContext.Provider value={selectionsViewerContext}>
      <SelectionsViewer
        tilesRefreshLastTimestamp={tilesRefreshLastTimestamp}
        scene={scene}
        setScene={setScene}
        onClose={() => navigate({ search: {} as never })}
      />
    </SelectionsViewerContext.Provider>
  );
}

export const Route = createFileRoute("/demo/photoreal-selections")({
  component: PhotorealSelections,
  staticData: { isPublicPage: true },
});
